import React, { useState } from 'react'
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import Svg, { Circle, G, Line, Text as SvgText } from 'react-native-svg'
import JocComponent from '@/Components/Components/JocComponent'
import { useGlobal } from '@/Components/Context/globalContext'
import { Audio } from 'expo-av'

const NOTE_SOUNDS: Record<string, any> = {
  Do:  require('@/assets/Sounds/do.mp3'),
  Re:  require('@/assets/Sounds/re.mp3'),
  Mi:  require('@/assets/Sounds/mi.mp3'),
  Fa:  require('@/assets/Sounds/fa.mp3'),
  Sol: require('@/assets/Sounds/sol.mp3'),
  La:  require('@/assets/Sounds/la.mp3'),
  Si:  require('@/assets/Sounds/si.mp3'),
}

const playNoteSound = async (noteName: string) => {
  const source = NOTE_SOUNDS[noteName]
  if (!source) return
  try {
    const { sound } = await Audio.Sound.createAsync(source, { shouldPlay: true, volume: 0.8 })
    sound.setOnPlaybackStatusUpdate((status) => {
      if (status.isLoaded && status.didJustFinish) sound.unloadAsync()
    })
  } catch {}
}

interface NoteData {
  name: string
  letter: string
  isIntruder: boolean
}

// 7 notes on the staff in appearance order
const NOTES: NoteData[] = [
  { name: 'Mi',  letter: 'I', isIntruder: false }, // idx 0
  { name: 'La',  letter: 'T', isIntruder: false }, // idx 1 (valid T)
  { name: 'Do',  letter: 'A', isIntruder: false }, // idx 2
  { name: 'Sol', letter: 'Z', isIntruder: false }, // idx 3
  { name: 'La',  letter: 'T', isIntruder: true  }, // idx 4 (intruder)
  { name: 'Re',  letter: 'G', isIntruder: false }, // idx 5
  { name: 'Fa',  letter: 'L', isIntruder: false }, // idx 6
]

// Tap order to spell GILTZA: G=Re(5) I=Mi(0) L=Fa(6) T=La-valid(1) Z=Sol(3) A=Do(2)
const CORRECT_ORDER = [5, 0, 6, 1, 3, 2]
const KEYWORD = 'GILTZA'

// Treble clef staff: 5 lines at y=10,20,30,40,50 (spacing=10, top to bottom)
// Line 5=F5, Line 4=D5, Line 3=B4, Line 2=G4(Sol), Line 1=E4(Mi)
const STAFF_LINE_Y = [10, 20, 30, 40, 50]
const NOTE_Y: Record<string, number> = {
  'La':  35, // A4 — space between lines 2 and 3
  'Sol': 40, // G4 — line 2
  'Fa':  45, // F4 — space between lines 1 and 2
  'Mi':  50, // E4 — line 1 (bottom)
  'Re':  55, // D4 — space below bottom staff line
  'Do':  60, // C4 — ledger line below staff
}
const NOTE_X = [70, 112, 152, 192, 232, 272, 312]

const NOTE_EQUIVALENCES = [
  { note: 'Do', letter: 'A' },
  { note: 'Re', letter: 'G' },
  { note: 'Mi', letter: 'I' },
  { note: 'Fa', letter: 'L' },
  { note: 'Sol', letter: 'Z' },
  { note: 'La', letter: 'T' },
]

export default function JocPartitura(RouterData: any) {
  const { textRecurs } = useGlobal()
  const [selectedOrder, setSelectedOrder] = useState<number[]>([])
  const [errors, setErrors] = useState(0)
  const [errorMsg, setErrorMsg] = useState('')

  const foundCount = selectedOrder.length
  const punts = Math.max(100 - Math.floor(errors / 3) * 25, 0)

  const navigateToReward = () => {
    RouterData.navigation.replace('Reward', {
      ...RouterData.route.params,
      punts,
    })
  }

  const handleNoteTap = (noteIdx: number) => {
    if (foundCount >= 6) return
    playNoteSound(NOTES[noteIdx].name)
    const expected = CORRECT_ORDER[foundCount]
    const isCorrect = noteIdx === expected ||
      (NOTES[noteIdx].name === NOTES[expected].name)
    if (isCorrect) {
      const next = [...selectedOrder, noteIdx]
      setSelectedOrder(next)
      setErrorMsg('')
      if (next.length === 6) setTimeout(navigateToReward, 800)
    } else {
      setErrors(e => e + 1)
      setErrorMsg(textRecurs('joc_partitura_error_nota'))
      setTimeout(() => setErrorMsg(''), 2500)
    }
  }

  return (
    <JocComponent RouterData={RouterData} setPistesDemanades={setErrors}>
      <ScrollView
        style={styles.panel}
        contentContainerStyle={styles.panelContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Keyword progress */}
        <View style={styles.keywordRow}>
          {KEYWORD.split('').map((letter, i) => {
            const filled = i < foundCount
            return (
              <View key={i} style={[styles.letterBox, filled && styles.letterBoxFilled]}>
                {filled && <Text style={styles.letterBoxText}>{letter}</Text>}
              </View>
            )
          })}
        </View>

        <Text style={styles.instruction}>
          {textRecurs('joc_partitura_instruccions')}
        </Text>

        {/* Score and error */}
        <View style={styles.scoreRow}>
          <Text style={styles.scoreText}>{punts} {textRecurs('general_puntos').toLowerCase()}</Text>
          {!!errorMsg && (
            <Text style={styles.errorText}>{errorMsg}</Text>
          )}
        </View>

        {/* Musical staff */}
        <View style={styles.staffWrapper}>
          <View style={{ width: 350, height: 88 }}>
            <Svg width={350} height={88}>
              {STAFF_LINE_Y.map(y => (
                <Line key={y} x1={48} y1={y} x2={338} y2={y} stroke="#444" strokeWidth={1.5} />
              ))}
              <SvgText x={8} y={56} fontSize={46} fill="#333">{'𝄞'}</SvgText>
              {NOTES.map((note, idx) => {
                const x = NOTE_X[idx]
                const y = NOTE_Y[note.name]
                const isSelected = selectedOrder.includes(idx)
                const color = isSelected ? '#00491F' : '#222'
                return (
                  <G key={idx}>
                    {note.name === 'Do' && (
                      <Line x1={x - 11} y1={60} x2={x + 11} y2={60} stroke="#444" strokeWidth={1.5} />
                    )}
                    <Line x1={x + 6} y1={y} x2={x + 6} y2={y - 28} stroke={color} strokeWidth={1.5} />
                    <Circle cx={x} cy={y} r={6} fill={color} />
                    <SvgText x={x} y={80} fontSize={10} fill="#555" textAnchor="middle">{note.name}</SvgText>
                  </G>
                )
              })}
            </Svg>
            {NOTES.map((note, idx) => {
              const x = NOTE_X[idx]
              const y = NOTE_Y[note.name]
              return (
                <TouchableOpacity
                  key={idx}
                  style={{ position: 'absolute', left: x - 14, top: y - 34, width: 28, height: 48 }}
                  onPress={() => handleNoteTap(idx)}
                  activeOpacity={0.7}
                />
              )
            })}
          </View>
        </View>

        <Text style={styles.tapHint}>{textRecurs('joc_partitura_tap_nota')}</Text>

        {/* Note equivalence table */}
        <Text style={styles.tableTitle}>{textRecurs('joc_partitura_nota_lletra')}</Text>
        <View style={styles.table}>
          {NOTE_EQUIVALENCES.map(({ note, letter }) => (
            <View key={note} style={styles.tableRow}>
              <Text style={styles.tableCellNote}>{note}</Text>
              <Text style={styles.tableCellEq}>=</Text>
              <Text style={styles.tableCellLetter}>{letter}</Text>
            </View>
          ))}
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>
    </JocComponent>
  )
}

const styles = StyleSheet.create({
  panel: {
    backgroundColor: '#f9f7e8',
    borderTopEndRadius: 44,
    borderTopStartRadius: 44,
    flex: 10,
  },
  panelContent: {
    paddingTop: 20,
    paddingHorizontal: 20,
  },
  keywordRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    marginBottom: 12,
  },
  letterBox: {
    width: 40,
    height: 44,
    borderWidth: 2,
    borderColor: '#555',
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  letterBoxFilled: {
    backgroundColor: '#00491F',
    borderColor: '#00491F',
  },
  letterBoxText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
    fontFamily: 'Poppins',
  },
  instruction: {
    textAlign: 'center',
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#333',
    marginBottom: 10,
    lineHeight: 20,
  },
  scoreRow: {
    alignItems: 'center',
    marginBottom: 8,
    minHeight: 42,
  },
  scoreText: {
    fontSize: 15,
    fontWeight: 'bold',
    fontFamily: 'Poppins',
    color: '#333',
  },
  errorText: {
    color: '#c0392b',
    fontSize: 13,
    fontFamily: 'Poppins',
    textAlign: 'center',
    marginTop: 4,
  },
  staffWrapper: {
    alignItems: 'center',
    marginVertical: 4,
  },
  tapHint: {
    textAlign: 'center',
    fontSize: 11,
    color: '#888',
    fontFamily: 'Poppins',
    marginTop: 4,
    marginBottom: 16,
  },
  tableTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    fontFamily: 'Poppins',
    color: '#333',
    marginBottom: 10,
    textAlign: 'center',
  },
  table: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    minWidth: 80,
    justifyContent: 'center',
  },
  tableCellNote: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#333',
    fontWeight: '600',
  },
  tableCellEq: {
    fontSize: 13,
    color: '#888',
    marginHorizontal: 6,
  },
  tableCellLetter: {
    fontSize: 16,
    fontFamily: 'Poppins',
    fontWeight: 'bold',
    color: '#00491F',
  },
})
